import { 
  LayoutDashboard,
  Users,
  AlertTriangle,
  CheckSquare,
  Building,
  Settings,
  ChevronLeft,
  ArrowLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { supervisorQueues } from '@sd/constants/supervisorNavQueues';
import { useNavigate } from 'react-router-dom';
import { sd } from '@sd/lib/sdPaths';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'layout-dashboard': LayoutDashboard,
  'users': Users,
  'alert-triangle': AlertTriangle,
  'check-square': CheckSquare,
  'building': Building,
};

interface SupervisorSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeView: string;
  onViewChange: (viewId: string) => void;
}

export function SupervisorSidebar({ 
  collapsed, 
  onToggle, 
  activeView, 
  onViewChange 
}: SupervisorSidebarProps) {
  const navigate = useNavigate();

  return (
    <aside 
      className={cn(
        "h-full bg-sidebar border-r-2 border-sidebar-border flex flex-col transition-all duration-300",
        collapsed ? "w-14" : "w-56"
      )}
    >
      <div className="p-3 flex items-center justify-between border-b border-sidebar-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(sd('admin'))}
              className="h-7 w-7 text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-sm text-sidebar-foreground">Supervisor</span>
          </div>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggle}
          className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin py-2">
        {!collapsed && (
          <div className="px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Views
            </span>
          </div>
        )}
        
        <nav className="space-y-0.5 px-2">
          {supervisorQueues.map((queue) => {
            const Icon = iconMap[queue.icon] || LayoutDashboard;
            const isActive = activeView === queue.id;
            const isAlert = queue.id === 'escalations' && queue.count > 0;
            
            return (
              <button
                key={queue.id}
                onClick={() => onViewChange(queue.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  isActive 
                    ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  collapsed && "justify-center px-2"
                )}
              >
                <Icon className={cn(
                  "h-4 w-4 flex-shrink-0",
                  isAlert && "text-destructive"
                )} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{queue.label}</span>
                    {queue.count > 0 && (
                      <span className={cn(
                        "text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center",
                        isAlert 
                          ? "bg-destructive/20 text-destructive" 
                          : "bg-muted text-muted-foreground"
                      )}>
                        {queue.count}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="p-2 border-t border-sidebar-border">
        <button
          onClick={() => navigate(sd('admin/settings'))}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors",
            collapsed && "justify-center px-2"
          )}
        >
          <Settings className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </button>
      </div>
    </aside>
  );
}
